import { CommonModule } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
    IContact,
    ICustomerDetails,
    IEmailContact,
    IUserRead,
} from '@dxp/core/models';
import {
    AgentDirectoryService,
    ContactDirectoryService,
    CustomerDirectoryService,
} from '@dxp/core/services';
import {
    catchError,
    concat,
    defaultIfEmpty,
    filter,
    map,
    mergeMap,
    Observable,
    of,
    Subject,
    take,
} from 'rxjs';
import { AvatarVariant } from '@dxp/core/types';
import { TelXLAvatarComponent } from '../avatar/avatar.component';

type Allowed = string | IEmailContact;
type ValidatorFn<T> = (value: T, index: number, all: T[]) => string | null;

interface IListAdapter<T> {
    toText(item: T): string;
    fromText(prev: T | undefined, text: string): T;
    createEmpty(): T;
}

interface IEmailLookup {
    name: string | null;
    kind: AvatarVariant;
}

function isEmailContact(x: unknown): x is IEmailContact {
    return (
        !!x && typeof x === 'object' && typeof (x as any).address === 'string'
    );
}

const STRING_ADAPTER: IListAdapter<string> = {
    toText: x => x ?? '',
    fromText: (_prev, text) => text,
    createEmpty: () => '',
};

const EMAIL_ADAPTER: IListAdapter<IEmailContact> = {
    toText: c => (c?.address ?? '').toLowerCase(),
    fromText: (prev, text) => ({
        ...(prev ?? { name: '' }),
        address: (text ?? '').toLowerCase(),
    }),
    createEmpty: () => ({ address: '', name: '' }),
};

@Component({
    selector: 'telxl-input-list',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, TelXLAvatarComponent],
    templateUrl: './input-list.component.html',
    styleUrl: './input-list.component.scss',
})
export class TelXLInputListComponent<T extends Allowed = string> {
    readonly #destroyRef = inject(DestroyRef);
    readonly #injector = inject(Injector);
    readonly #customerDirectoryService = inject(CustomerDirectoryService);
    readonly #contactDirectoryService = inject(ContactDirectoryService);
    readonly #agentDirectoryService = inject(AgentDirectoryService);

    @ViewChild('scrollContainer', { static: true })
    private scrollContainer!: ElementRef<HTMLDivElement>;

    kind = input<'string' | 'emailcontact' | null>(null);
    items = input<T[]>([]);
    size = input<'sm' | 'md' | 'lg'>('md');
    minLength = input(0);
    maxLength = input(0);
    allowDuplicates = input(true);
    validateEmail = input(false);
    mode = input<('edit' | 'add' | 'remove' | 'directory')[]>([
        'edit',
        'add',
        'remove',
    ]);
    maxRows = input<number | null>(null);
    active = input<boolean>(false);
    lock = input<number[]>([]);
    isValidChange = output<boolean>();
    itemsChange = output<T[]>();
    event = output<'directory' | 'add'>();

    directoryHover = signal<boolean>(false);
    addHover = signal<boolean>(false);
    prevCount = signal<number>(0);

    readonly maxHeight = computed<number | null>(() => {
        if (this.maxRows() === null) return null;

        // const small = [40, 74, 110, 144, 180];
        // const medium = [46, 88, 130, 172, 214];
        // const large = [56, 108, 160, 212, 264];

        switch (this.size()) {
            case 'lg':
                return 52 * (this.maxRows() as number) + 4;
            case 'sm':
                return 35 * (this.maxRows() as number) + 5;
            default:
                return 42 * (this.maxRows() as number) + 4;
        }
    });

    readonly sizeClass = computed<string | null>(() => {
        switch (this.size()) {
            case 'lg':
                return 'form-control-lg';
            case 'sm':
                return 'form-control-sm';
            default:
                return null;
        }
    });

    readonly buttonSizeClass = computed(() => {
        switch (this.size()) {
            case 'lg':
                return 'btn-lg';
            case 'sm':
                return 'btn-sm';
            default:
                return null;
        }
    });

    readonly itemsToRender = computed<T[]>(() =>
        this.items().filter(i => !this.isPending(i)),
    );

    readonly adapter = computed<IListAdapter<T>>(() => {
        const hint = this.kind();
        if (hint === 'string')
            return STRING_ADAPTER as unknown as IListAdapter<T>;
        if (hint === 'emailcontact')
            return EMAIL_ADAPTER as unknown as IListAdapter<T>;

        const all = this.items() as unknown[];
        const sample = all.find(x => x != null);

        if (typeof sample === 'string')
            return STRING_ADAPTER as unknown as IListAdapter<T>;
        if (isEmailContact(sample))
            return EMAIL_ADAPTER as unknown as IListAdapter<T>;

        return STRING_ADAPTER as unknown as IListAdapter<T>;
    });

    readonly validationErrors = signal<string[]>([]);
    readonly valid = computed(() => this.validationErrors().length === 0);

    readonly #validators = computed<ValidatorFn<T>[]>(() => {
        const rules: ValidatorFn<T>[] = [];
        const toText = this.adapter().toText;

        if (this.minLength() > 0) {
            const min = this.minLength();
            rules.push(v =>
                toText(v).length >= min ? null : `Minimum length ${min}`,
            );
        }
        if (this.maxLength() > 0) {
            const max = this.maxLength();
            rules.push(v =>
                toText(v).length <= max ? null : `Maximum length ${max}`,
            );
        }
        if (this.validateEmail()) {
            const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            rules.push(v =>
                emailRx.test(toText(v)) ? null : 'Invalid email format',
            );
        }
        return rules;
    });

    #searchCustomerDirectory(
        emailAddress: string,
    ): Observable<ICustomerDetails | null> {
        return this.#customerDirectoryService
            .fetchCustomerDirectory(
                1,
                0,
                undefined,
                emailAddress,
                undefined,
                true,
                true,
                false,
            )
            .pipe(
                map(res =>
                    res.customerDirectory.length > 0
                        ? res.customerDirectory[0]
                        : null,
                ),
                catchError(() => of(null)),
            );
    }

    #searchAgentDirectory(emailAddress: string): Observable<IUserRead | null> {
        return this.#agentDirectoryService
            .fetchAgentDirectory(
                1,
                0,

                emailAddress,

                true,
                true,
                false,
            )
            .pipe(
                map(res => (res.results.length > 0 ? res.results[0] : null)),
                catchError(() => of(null)),
            );
    }

    #searchContactDirectory(emailAddress: string): Observable<IContact | null> {
        return this.#contactDirectoryService
            .fetchContactDirectory(10, 0, emailAddress, 0, true, true, false)
            .pipe(
                map(res => (res.results.length > 0 ? res.results[0] : null)),
                catchError(() => of(null)),
            );
    }

    readonly #emailLookups = signal<Map<string, IEmailLookup>>(new Map());

    readonly #lookupQueue = new Subject<string>();
    readonly #pending = signal<Set<string>>(new Set());
    readonly #emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    readonly emailMode = computed<boolean>(() => {
        const hint = this.kind();
        if (hint === 'emailcontact') return true;
        if (hint === 'string') return false;
        const sample = (this.items() as unknown[]).find(x => x != null);
        return isEmailContact(sample);
    });

    #addPending(addr: string) {
        this.#pending.update(s => {
            const n = new Set(s);
            n.add(addr);
            return n;
        });
    }

    #removePending(addr: string) {
        this.#pending.update(s => {
            const n = new Set(s);
            n.delete(addr);
            return n;
        });
    }

    #hasPending(addr: string) {
        return this.#pending().has(addr);
    }

    #watchEmailAddresses() {
        effect(() => {
            if (!this.emailMode()) return;

            const toText = this.adapter().toText;
            const addresses = this.items()
                .map(it => toText(it))
                .map(s => this.#normalise(s))
                .filter(s => !!s && this.#emailRx.test(s));

            const current = this.#emailLookups();

            for (const addr of addresses) {
                if (!current.has(addr) && !this.#hasPending(addr)) {
                    this.#addPending(addr);
                    this.#lookupQueue.next(addr);
                }
            }
        });
    }

    #setupLookupPipeline() {
        this.#lookupQueue
            .pipe(
                mergeMap(addr => this.#lookupEmail(addr)),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe(({ addr, lookup }) => {
                this.#removePending(addr);
                if (lookup) {
                    this.#emailLookups.update(m => {
                        const n = new Map(m);
                        n.set(addr, lookup);
                        return n;
                    });

                    if (lookup.name) {
                        const toText = this.adapter().toText;

                        const current = this.items();
                        let changed = false;

                        const next = current.map(it => {
                            if (
                                isEmailContact(it) &&
                                this.#normalise(toText(it)) ===
                                    this.#normalise(addr) &&
                                (!it.name || it.name.length === 0)
                            ) {
                                changed = true;
                                return {
                                    ...(it as IEmailContact),
                                    name: lookup.name,
                                } as typeof it;
                            }
                            return it;
                        });

                        if (changed) {
                            this.itemsChange.emit(next as any);
                        }
                    }
                }
            });
    }

    #lookupEmail(
        addr: string,
    ): Observable<{ addr: string; lookup: IEmailLookup | null }> {
        const wrap = <T>(
            obs: Observable<T | null>,
            mapTo: (x: NonNullable<T>) => IEmailLookup,
        ) => obs.pipe(map(x => (x ? mapTo(x) : null)));

        const customer$ = wrap(this.#searchCustomerDirectory(addr), c => ({
            name: c.name,
            kind: 'end-customers' as const,
        }));

        const contact$ = wrap(this.#searchContactDirectory(addr), c => ({
            name: c.fullName,

            kind: 'external-contacts' as const,
        }));

        const agent$ = wrap(this.#searchAgentDirectory(addr), a => ({
            name: a.name,
            kind: 'internal-directory' as const,
        }));

        return concat(customer$, contact$, agent$).pipe(
            filter((lkp): lkp is IEmailLookup => lkp !== null),
            take(1),
            map(lookup => ({ addr, lookup })),
            defaultIfEmpty({
                addr,
                lookup: { name: null, kind: 'default' as const },
            }),
            catchError(() => of({ addr, lookup: null })),
        );
    }

    readonly #normalise = (s: string | null | undefined) =>
        (s ?? '').trim().toLowerCase();

    readonly #duplicateFlags = computed<boolean[]>(() => {
        if (this.allowDuplicates()) return [];
        const toText = this.adapter().toText;
        const items = this.items();
        const normaliseKey = (x: T) => {
            const s = toText(x);
            return this.emailMode() ? this.#normalise(s) : s;
        };

        const keys = items.map(normaliseKey);
        const counts = new Map<string, number>();
        for (const k of keys) counts.set(k, (counts.get(k) ?? 0) + 1);
        return keys.map(k => (counts.get(k) ?? 0) > 1);
    });

    constructor() {
        effect(() => {
            const all = this.items();
            const rules = this.#validators();
            const errors: string[] = [];
            const dupFlags = this.#duplicateFlags();

            for (let i = 0; i < all.length; i++) {
                if (!this.allowDuplicates() && dupFlags[i]) {
                    errors.push('Duplicate entry not allowed');
                    continue;
                }

                const v = all[i];
                for (const rule of rules) {
                    const e = rule(v, i, all);
                    if (e) {
                        errors.push(e);
                        break;
                    }
                }
            }
            this.validationErrors.set(errors);
            this.isValidChange.emit(errors.length === 0);

            if (!this.emailMode()) return;

            const current = this.items();
            let changed = false;

            const next = current.map(it => {
                if (isEmailContact(it)) {
                    const lower = this.#normalise(it.address);
                    if (it.address !== lower) {
                        changed = true;
                        return {
                            ...(it as IEmailContact),
                            address: lower,
                        } as typeof it;
                    }
                }
                return it;
            });

            if (changed) {
                this.itemsChange.emit(next as T[]);
            }
        });

        effect(() => {
            const count = this.itemsToRender().length;

            if (count > this.prevCount()) {
                afterNextRender(() => this.#scrollToBottom(), {
                    injector: this.#injector,
                });
            }

            this.prevCount.set(count);
        });

        this.#setupLookupPipeline();
        this.#watchEmailAddresses();
    }

    onAdd() {
        const empty = this.adapter().createEmpty();
        this.itemsChange.emit([...this.items(), empty]);
        this.event.emit('add');

        afterNextRender(() => this.#scrollToBottom(), {
            injector: this.#injector,
        });
    }

    onDelete(index: number) {
        const next = this.items().filter((_, i) => i !== index);
        this.itemsChange.emit(next);
    }

    onEnter(event: Event) {
        event.preventDefault();
        (event.target as HTMLInputElement).blur();
    }

    onItemChange(text: string, index: number) {
        const next = [...this.items()];
        const fromText = this.adapter().fromText;
        next[index] = fromText(next[index], text);

        if (this.emailMode()) {
            const addr = this.#normalise(text);
            if (
                this.#emailRx.test(addr) &&
                !this.#emailLookups().has(addr) &&
                !this.#hasPending(addr)
            ) {
                this.#addPending(addr);
                this.#lookupQueue.next(addr);
            } else if (
                this.#emailLookups().has(addr) &&
                isEmailContact(next[index])
            ) {
                const lookup = this.#emailLookups().get(addr);

                if (
                    lookup &&
                    (!next[index].name || next[index].name.length === 0)
                ) {
                    next[index] = {
                        ...(next[index] as IEmailContact),
                        name: lookup.name ?? next[index].name,
                    } as T;
                }
            }
        }

        this.itemsChange.emit(next);
    }

    canRenderAvatar(item: Allowed): boolean {
        if (isEmailContact(item)) {
            const addr = this.#normalise(item.address);
            const lookup = this.#emailLookups().has(addr)
                ? this.#emailLookups().get(addr)?.kind
                : undefined;
            return !!lookup && lookup !== 'default';
        } else {
            return false;
        }
    }

    getAvatarVariant(item: Allowed): AvatarVariant {
        if (isEmailContact(item)) {
            const addr = this.#normalise(item.address);
            return this.#emailLookups().get(addr)?.kind ?? 'default';
        } else {
            return 'default';
        }
    }

    getAvatarName(item: Allowed): string {
        if (isEmailContact(item)) {
            const addr = this.#normalise(item.address);
            return this.#emailLookups().get(addr)?.name ?? '';
        } else {
            return '';
        }
    }

    isItemInvalid(index: number): boolean {
        const all = this.items();

        if (!this.allowDuplicates() && this.#duplicateFlags()[index]) {
            return true;
        }
        const value = all[index];
        for (const rule of this.#validators()) {
            if (rule(value, index, all)) return true;
        }
        return false;
    }

    isPending(item: T): boolean {
        if (!this.emailMode()) return false;
        const addr = this.#normalise(this.adapter().toText(item));
        return this.#hasPending(addr);
    }

    onDirectory() {
        this.event.emit('directory');
    }

    trackByIndex(index: number, _item: T): number {
        return index;
    }

    #scrollToBottom() {
        const el = this.scrollContainer?.nativeElement;
        if (!el) return;

        const hasOverflow = el.scrollHeight > el.clientHeight;
        if (hasOverflow) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
    }
}
